import axios, { AxiosRequestConfig } from 'axios'
import { initCache, transformLanguage } from './utils'

import { Modules } from '../util/modules'
import config from '../config'
import { createI18n } from 'vue-i18n'
import enMessages from '../locales/en.json'
import daMessages from '../locales/da.json'
import deMessages from '../locales/de.json'
import esMessages from '../locales/es.json'
import fiMessages from '../locales/fi.json'
import frCAMessages from '../locales/fr-CA.json'
import frFRMessages from '../locales/fr-FR.json'
import itMessages from '../locales/it.json'
import nlMessages from '../locales/nl.json'
import noMessages from '../locales/no.json'
import ptBRMessages from '../locales/pt-BR.json'
import ptPTMessages from '../locales/pt-PT.json'
import svMessages from '../locales/sv.json'
import { locales, getChatWidgetSupportedLocales } from '../util/locales'
import { useStore } from '../store'
import { RouterRegistry } from '../util/routerRegistry'

interface RetryConfig extends AxiosRequestConfig {
  retry: number
  retryDelay: number
}

declare global {
  interface Window {
    __HOST_CONTEXT__: {
      i18n: any
      piniaStores?: any
    }
  }
}

export const localizationKey = 'locale'
export enum Languages {
  English_US = 'en-US',
  Danish = 'da',
  German = 'de',
  Spanish = 'es',
  Finnish = 'fi',
  French_Canada = 'fr-CA',
  French_France = 'fr-FR',
  Italian = 'it',
  Dutch = 'nl',
  Norwegian = 'no',
  Portuguese_Brazil = 'pt-BR',
  Portuguese_Portugal = 'pt-PT',
  Swedish = 'sv',
}

export const retryConfig: RetryConfig = {
  retry: 4, // total 5 retries
  retryDelay: 1000,
}

export const i18n = createI18n({
  legacy: false,
  locale: Languages.English_US,
  silentFallbackWarn: true,
  globalInjection: true,
  silentTranslationWarn: process.env.VUE_APP_SILENT_WARNINGS === 'true',
  messages: {
    [Languages.English_US]: enMessages,
    [Languages.Danish]: daMessages,
    [Languages.German]: deMessages,
    [Languages.Spanish]: esMessages,
    [Languages.Finnish]: fiMessages,
    [Languages.French_Canada]: frCAMessages,
    [Languages.French_France]: frFRMessages,
    [Languages.Italian]: itMessages,
    [Languages.Dutch]: nlMessages,
    [Languages.Norwegian]: noMessages,
    [Languages.Portuguese_Brazil]: ptBRMessages,
    [Languages.Portuguese_Portugal]: ptPTMessages,
    [Languages.Swedish]: svMessages,
  },
  missing: (_, key) => {
    return key
  },
})
window.__HOST_CONTEXT__ = window.__HOST_CONTEXT__ || {}
window.__HOST_CONTEXT__.i18n = i18n

axios.interceptors.response.use(undefined, err => {
  const { config, message } = err
  if (!config || !config.retry) {
    return Promise.reject(err)
  }
  // retry while Network timeout or Network Error
  if (!(message.includes('timeout') || message.includes('Network Error'))) {
    return Promise.reject(err)
  }

  --config.retry
  const delayRetryRequest = new Promise<void>(resolve => {
    setTimeout(() => {
      resolve()
    }, config.retryDelay || 1000)
  })
  return delayRetryRequest.then(() => axios(config))
})

// Will be cached till page-refresh
const { setCacheAndGetToLoad } = initCache()

export async function updateLocale(
  language: Languages,
  modules: Modules[] = [Modules.Common, Modules.SmartList, Modules.ContactDetail, Modules.Copilot],
  forceUpdate: boolean = false
) {
  // Check if we're on a login route and should skip locale update to prevent jitter
  // Allow manual language changes (from language dropdown) even on login routes
  const isManualLanguageChange = modules.includes(Modules.Login)

  if (!forceUpdate && !isManualLanguageChange) {
    try {
      const store = useStore()
      const router = RouterRegistry.getRouter()
      const loginRouteNames = ['login', 'login_sso', 'login_page', 'login_support', 'login_desktop', 'shopify_login', 'eject-login']
      const currentRouteName = router?.currentRoute?.value?.name as string | undefined

      // If we're on a login route and user data exists, skip locale update to prevent jitter
      // The locale will be updated when navigating away from login to the logged-in screens
      // Exception: Allow manual language changes from the language dropdown
      if (currentRouteName && loginRouteNames.includes(currentRouteName) && store.state.user?.user) {
        // Skip locale update while on login screen to prevent language jitter
        // Store the desired language in localStorage so it can be applied after navigation
        const transformedLanguage = transformLanguage(language)
        localStorage.setItem('locale', transformedLanguage)
        return false
      }
    } catch (error) {
      // If we can't check the route, continue with the update
      console.log('Error checking route in updateLocale:', error)
    }
  }

  let transformedLanguage = transformLanguage(language)
  try {
    const store = useStore()
    const isEnglishLocaleForced = store.getters['supportPreferences/isEnglishLocaleForced']
    if (isEnglishLocaleForced) transformedLanguage = 'en-US'
  } catch (error) {
    console.log(error)
  }
  localStorage.setItem('locale', transformedLanguage)

  const modulesToLoad = setCacheAndGetToLoad(transformedLanguage as Languages, modules as Modules[])
  if (!modulesToLoad.length) {
    i18n.global.locale.value = transformedLanguage
    i18n.global.fallbackLocale.value = Languages.English_US
    return false
  }
  const localizationURL = `${config.localizationApiURL}/${transformedLanguage}/?module=${modulesToLoad.join(',')}`

  await axios
    .get(localizationURL, {
      ...retryConfig,
    })
    .then(response => {
      const localJSONs = response.data
      i18n.global.locale.value = transformedLanguage
      i18n.global.fallbackLocale.value = Languages.English_US

      if (transformedLanguage !== Languages.English_US) {
        i18n.global.mergeLocaleMessage(transformedLanguage, localJSONs[transformedLanguage])
      }

      i18n.global.mergeLocaleMessage(Languages.English_US, localJSONs[Languages.English_US])
      //  Set status
    })
    .catch(error => {
      // Note: The 'error' is sent to Sentry after the API call is failed, even after retrying for 5 times
      console.error(
        `Err:: Translations failed to load for ${transformedLanguage}_${modulesToLoad.join(',')} -  ${JSON.stringify(
          error
        )}`
      )
      //  Set status
    })
}

// Export locales data for use in other parts of the application
export { locales, getChatWidgetSupportedLocales }
